import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, Save, Upload, Users, Video, Eye, Bell, 
  Edit, Trash2, Plus, Image as ImageIcon, Loader2, 
  CheckCircle, Instagram, Twitter, Youtube, Link2,
  Calendar, MessageSquare, Settings, BarChart3, X
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CreatorDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [videos, setVideos] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [stats, setStats] = useState({});
  const [showPostModal, setShowPostModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  
  // Form state for profile editing
  const [profileForm, setProfileForm] = useState({
    name: '',
    description: '',
    profile_image_url: '',
    banner_image_url: '',
    social_links: []
  });

  // Get auth info
  const getAuthToken = () => {
    return localStorage.getItem('vodToken') || localStorage.getItem('livestreamToken');
  };
  
  const getCreatorId = () => {
    return localStorage.getItem('creatorProfileId');
  };

  // Load creator data
  useEffect(() => {
    const fetchData = async () => {
      const token = getAuthToken();
      const creatorId = getCreatorId();
      
      if (!token || !creatorId) {
        navigate('/vod');
        return;
      }
      
      setLoading(true);
      try {
        const headers = { Authorization: `Bearer ${token}` };
        
        // Fetch profile, posts, videos in parallel
        const [profileRes, postsRes, videosRes] = await Promise.all([
          axios.get(`${API}/api/creators/profiles/${creatorId}/dashboard`, { headers }),
          axios.get(`${API}/api/creators/${creatorId}/posts`, { headers }),
          axios.get(`${API}/api/creators/${creatorId}/videos`, { headers })
        ]);
        
        const profileData = profileRes.data.profile;
        setProfile(profileData);
        setProfileForm({
          name: profileData.name || '',
          description: profileData.description || '',
          profile_image_url: profileData.profile_image_url || '',
          banner_image_url: profileData.banner_image_url || '',
          social_links: profileData.social_links || []
        });
        
        setPosts(postsRes.data.posts || []);
        setVideos(videosRes.data.videos || []);
        setStats(profileRes.data.stats || {});
        setSubscribers(profileRes.data.subscribers || []);
        
      } catch (err) {
        console.error('Error loading creator data:', err);
        if (err.response?.status === 401 || err.response?.status === 403) {
          navigate('/vod');
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [navigate]);

  // Save profile changes
  const handleSaveProfile = async () => {
    const token = getAuthToken();
    const creatorId = getCreatorId();
    
    setSaving(true);
    try {
      await axios.put(
        `${API}/api/creators/profiles/${creatorId}`,
        profileForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh profile
      const res = await axios.get(
        `${API}/api/creators/profiles/${creatorId}/dashboard`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProfile(res.data.profile);
      
      alert('Profile saved successfully!');
    } catch (err) {
      console.error('Error saving profile:', err);
      alert(err.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  // Upload image
  const handleImageUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const token = getAuthToken();
    const formData = new FormData();
    formData.append('file', file);
    
    // Use creator name/slug for folder organization
    const creatorName = profile?.name || profileForm.name || 'creator';
    const folderName = profile?.slug || creatorName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').trim();
    const imageType = type === 'profile' ? 'profile' : 'banner';
    formData.append('folder', `creators/${folderName}/${imageType}`);
    
    try {
      const res = await axios.post(`${API}/api/cloudflare/images/upload-file`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (res.data.success) {
        setProfileForm(prev => ({
          ...prev,
          [type === 'profile' ? 'profile_image_url' : 'banner_image_url']: res.data.url
        }));
      }
    } catch (err) {
      console.error('Upload error:', err);
      alert('Failed to upload image');
    }
  };

  // Add/update social link
  const addSocialLink = () => {
    setProfileForm(prev => ({
      ...prev,
      social_links: [...prev.social_links, { platform: 'instagram', url: '', username: '' }]
    }));
  };

  const updateSocialLink = (index, field, value) => {
    setProfileForm(prev => ({
      ...prev,
      social_links: prev.social_links.map((link, i) => 
        i === index ? { ...link, [field]: value } : link
      )
    }));
  };

  const removeSocialLink = (index) => {
    setProfileForm(prev => ({
      ...prev,
      social_links: prev.social_links.filter((_, i) => i !== index)
    }));
  };

  // Delete post
  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    
    const token = getAuthToken();
    const creatorId = getCreatorId();
    
    try {
      await axios.delete(`${API}/api/creators/${creatorId}/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(posts.filter(p => p.id !== postId));
    } catch (err) {
      console.error('Delete error:', err);
      alert('Failed to delete post');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-900 to-black flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-900 to-black text-white">
      {/* Header */}
      <header className="border-b border-gray-800 bg-gray-900/80 backdrop-blur-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/vod')}>
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-800">
                  {profile?.profile_image_url ? (
                    <img src={profile.profile_image_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-lg font-bold text-cyan-400">
                      {profile?.name?.charAt(0) || '?'}
                    </div>
                  )}
                </div>
                <div>
                  <h1 className="font-bold text-lg">{profile?.name}</h1>
                  <p className="text-sm text-gray-400">Creator Dashboard</p>
                </div>
              </div>
            </div>
            <Button 
              onClick={() => navigate(`/c/${profile?.slug}`)}
              variant="outline"
              className="border-cyan-500/50 text-cyan-400"
            >
              <Eye className="h-4 w-4 mr-2" /> View Channel
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-cyan-500/20">
                  <Users className="h-5 w-5 text-cyan-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{profile?.subscriber_count || 0}</p>
                  <p className="text-xs text-gray-400">Subscribers</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/20">
                  <Video className="h-5 w-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{profile?.video_count || 0}</p>
                  <p className="text-xs text-gray-400">Videos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/20">
                  <Eye className="h-5 w-5 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_views || 0}</p>
                  <p className="text-xs text-gray-400">Total Views</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-pink-500/20">
                  <MessageSquare className="h-5 w-5 text-pink-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{posts.length}</p>
                  <p className="text-xs text-gray-400">Posts</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-800 pb-4">
          <TabButton active={activeTab === 'profile'} onClick={() => setActiveTab('profile')}>
            <Settings className="h-4 w-4 mr-2" /> Profile
          </TabButton>
          <TabButton active={activeTab === 'posts'} onClick={() => setActiveTab('posts')}>
            <MessageSquare className="h-4 w-4 mr-2" /> Posts
          </TabButton>
          <TabButton active={activeTab === 'videos'} onClick={() => setActiveTab('videos')}>
            <Video className="h-4 w-4 mr-2" /> Videos
          </TabButton>
          <TabButton active={activeTab === 'subscribers'} onClick={() => setActiveTab('subscribers')}>
            <Users className="h-4 w-4 mr-2" /> Subscribers
          </TabButton>
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="space-y-6 max-w-3xl">
            <Card className="bg-gray-800/50 border-gray-700">
              <CardHeader>
                <CardTitle>Channel Appearance</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Banner */}
                <div>
                  <label className="text-sm text-gray-400 mb-2 block">Banner Image</label>
                  <div className="relative h-32 rounded-xl overflow-hidden bg-gradient-to-br from-cyan-900/30 to-purple-900/30">
                    {profileForm.banner_image_url && (
                      <img src={profileForm.banner_image_url} alt="" className="w-full h-full object-cover" />
                    )}
                    <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 hover:opacity-100 transition-opacity cursor-pointer">
                      <Upload className="h-8 w-8 text-white" />
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'banner')}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>

                {/* Profile Image */}
                <div>
                  <label className="text-sm text-gray-400 mb-2 block">Profile Image</label>
                  <div className="flex items-center gap-4">
                    <div className="relative w-20 h-20 rounded-full overflow-hidden bg-gray-700">
                      {profileForm.profile_image_url ? (
                        <img src={profileForm.profile_image_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-2xl font-bold text-cyan-400">
                          {profileForm.name?.charAt(0) || '?'}
                        </div>
                      )}
                    </div>
                    <label className="flex items-center gap-2 px-4 py-2 border border-gray-700 rounded-lg cursor-pointer hover:bg-gray-800 transition">
                      <Upload className="h-4 w-4" /> Upload Photo
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'profile')}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>

                {/* Name */}
                <div>
                  <label className="text-sm text-gray-400 mb-2 block">Channel Name</label>
                  <Input
                    value={profileForm.name}
                    onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                    className="bg-gray-900 border-gray-700"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="text-sm text-gray-400 mb-2 block">Description</label>
                  <textarea
                    value={profileForm.description}
                    onChange={(e) => setProfileForm({ ...profileForm, description: e.target.value })}
                    placeholder="Tell your subscribers about your channel..."
                    rows={4}
                    className="w-full p-3 rounded-lg bg-gray-900 border border-gray-700 text-white resize-none"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Social Links */}
            <Card className="bg-gray-800/50 border-gray-700">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Social Links</CardTitle>
                <Button variant="outline" size="sm" onClick={addSocialLink} className="border-gray-700">
                  <Plus className="h-4 w-4 mr-1" /> Add Link
                </Button>
              </CardHeader>
              <CardContent className="space-y-3">
                {profileForm.social_links.length === 0 ? (
                  <p className="text-gray-500 text-sm">No social links added yet</p>
                ) : (
                  profileForm.social_links.map((link, index) => (
                    <div key={index} className="flex gap-2 items-center">
                      <select
                        value={link.platform}
                        onChange={(e) => updateSocialLink(index, 'platform', e.target.value)}
                        className="p-2 rounded-lg bg-gray-900 border border-gray-700 text-white w-32"
                      >
                        <option value="instagram">Instagram</option>
                        <option value="tiktok">TikTok</option>
                        <option value="snapchat">Snapchat</option>
                        <option value="youtube">YouTube</option>
                        <option value="twitter">Twitter</option>
                        <option value="facebook">Facebook</option>
                        <option value="website">Website</option>
                      </select>
                      <Input
                        value={link.url}
                        onChange={(e) => updateSocialLink(index, 'url', e.target.value)}
                        placeholder="https://..."
                        className="bg-gray-900 border-gray-700 flex-1"
                      />
                      <Input
                        value={link.username || ''}
                        onChange={(e) => updateSocialLink(index, 'username', e.target.value)}
                        placeholder="@username"
                        className="bg-gray-900 border-gray-700 w-28"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeSocialLink(index)}
                        className="text-red-400"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button 
                onClick={handleSaveProfile}
                disabled={saving}
                className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 px-8"
              >
                {saving ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                ) : (
                  <><Save className="h-4 w-4 mr-2" /> Save Changes</>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Posts Tab */}
        {activeTab === 'posts' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-bold">Channel Posts</h2>
                <p className="text-gray-400 text-sm">Share updates with your subscribers</p>
              </div>
              <Button 
                onClick={() => { setEditingPost(null); setShowPostModal(true); }}
                className="bg-gradient-to-r from-cyan-600 to-blue-600"
              >
                <Plus className="h-4 w-4 mr-2" /> New Post
              </Button>
            </div>

            {posts.length === 0 ? (
              <div className="text-center py-16 bg-gray-800/30 rounded-xl border border-gray-800">
                <MessageSquare className="h-16 w-16 mx-auto mb-4 text-gray-600" />
                <p className="text-gray-500 mb-4">No posts yet</p>
                <Button 
                  onClick={() => { setEditingPost(null); setShowPostModal(true); }}
                  className="bg-gradient-to-r from-cyan-600 to-blue-600"
                >
                  <Plus className="h-4 w-4 mr-2" /> Create Your First Post
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {posts.map((post) => (
                  <Card key={post.id} className="bg-gray-800/50 border-gray-700">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="text-white whitespace-pre-wrap">{post.content}</p>
                          {post.image_url && (
                            <img 
                              src={post.image_url} 
                              alt="" 
                              className="mt-3 rounded-lg max-h-64 object-cover"
                            />
                          )}
                          <p className="text-xs text-gray-500 mt-3">
                            <Calendar className="h-3 w-3 inline mr-1" />
                            {new Date(post.created_at).toLocaleString()}
                          </p>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setEditingPost(post); setShowPostModal(true); }}
                            className="text-gray-400"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeletePost(post.id)}
                            className="text-red-400"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Videos Tab */}
        {activeTab === 'videos' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-bold">Your Videos</h2>
                <p className="text-gray-400 text-sm">Videos assigned to your channel</p>
              </div>
            </div>

            {videos.length === 0 ? (
              <div className="text-center py-16 bg-gray-800/30 rounded-xl border border-gray-800">
                <Video className="h-16 w-16 mx-auto mb-4 text-gray-600" />
                <p className="text-gray-500 mb-2">No videos assigned yet</p>
                <p className="text-gray-600 text-sm">Contact an admin to assign videos to your channel</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {videos.map((video) => (
                  <Card key={video.id} className="bg-gray-800/50 border-gray-700 overflow-hidden group">
                    <div className="aspect-video bg-gray-900 relative">
                      {video.thumbnail_url ? (
                        <img src={video.thumbnail_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Video className="h-12 w-12 text-gray-700" />
                        </div>
                      )}
                      {video.is_premium_exclusive && (
                        <Badge className="absolute top-2 right-2 bg-gradient-to-r from-amber-500 to-yellow-500">
                          Premium
                        </Badge>
                      )}
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-white line-clamp-2 mb-2">{video.title}</h3>
                      <div className="flex items-center gap-4 text-sm text-gray-400">
                        <span className="flex items-center gap-1">
                          <Eye className="h-3.5 w-3.5" />
                          {video.view_count || 0}
                        </span>
                        <span>{new Date(video.created_at).toLocaleDateString()}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Subscribers Tab */}
        {activeTab === 'subscribers' && (
          <div>
            <div className="mb-6">
              <h2 className="text-xl font-bold">Subscribers</h2>
              <p className="text-gray-400 text-sm">People subscribed to your channel</p>
            </div>

            {subscribers.length === 0 ? (
              <div className="text-center py-16 bg-gray-800/30 rounded-xl border border-gray-800">
                <Users className="h-16 w-16 mx-auto mb-4 text-gray-600" />
                <p className="text-gray-500">No subscribers yet</p>
                <p className="text-gray-600 text-sm mt-2">Share your channel to grow your audience!</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {subscribers.map((sub) => (
                  <Card key={sub.user_id} className="bg-gray-800/50 border-gray-700">
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center">
                        <Users className="h-5 w-5 text-gray-400" />
                      </div>
                      <div>
                        <p className="font-medium text-white">{sub.email || sub.user_id}</p>
                        <p className="text-xs text-gray-500">
                          Subscribed {new Date(sub.subscribed_at).toLocaleDateString()}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Post Modal */}
      {showPostModal && (
        <PostModal
          post={editingPost}
          creatorId={getCreatorId()}
          creatorSlug={profile?.slug}
          creatorName={profile?.name}
          onClose={() => { setShowPostModal(false); setEditingPost(null); }}
          onSave={(newPost) => {
            if (editingPost) {
              setPosts(posts.map(p => p.id === newPost.id ? newPost : p));
            } else {
              setPosts([newPost, ...posts]);
            }
            setShowPostModal(false);
            setEditingPost(null);
          }}
        />
      )}
    </div>
  );
};

// Tab Button Component
const TabButton = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
      active 
        ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white' 
        : 'text-gray-400 hover:text-white hover:bg-gray-800'
    }`}
  >
    {children}
  </button>
);

// Post Modal Component
const PostModal = ({ post, creatorId, creatorSlug, creatorName, onClose, onSave }) => {
  const [content, setContent] = useState(post?.content || '');
  const [imageUrl, setImageUrl] = useState(post?.image_url || '');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL;
  const getAuthToken = () => localStorage.getItem('vodToken') || localStorage.getItem('livestreamToken');

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const token = getAuthToken();
      const formData = new FormData();
      formData.append('file', file);
      
      // Use creator slug/name for organized folder structure
      const folderName = creatorSlug || (creatorName ? creatorName.toLowerCase().replace(/[^a-z0-9]+/g, '-') : creatorId);
      formData.append('folder', `creators/${folderName}/posts`);
      
      const res = await axios.post(`${API}/api/cloudflare/images/upload-file`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (res.data.success) {
        setImageUrl(res.data.url);
      }
    } catch (err) {
      console.error('Upload error:', err);
      alert('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!content.trim()) {
      alert('Please enter some content');
      return;
    }
    
    setSaving(true);
    try {
      const token = getAuthToken();
      const headers = { Authorization: `Bearer ${token}` };
      
      let result;
      if (post?.id) {
        // Update existing post
        result = await axios.put(
          `${API}/api/creators/${creatorId}/posts/${post.id}`,
          { content, image_url: imageUrl },
          { headers }
        );
      } else {
        // Create new post
        result = await axios.post(
          `${API}/api/creators/${creatorId}/posts`,
          { content, image_url: imageUrl },
          { headers }
        );
      }
      
      onSave(result.data.post);
    } catch (err) {
      console.error('Save error:', err);
      alert(err.response?.data?.detail || 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-2xl w-full max-w-lg">
        <form onSubmit={handleSubmit}>
          <div className="p-6 border-b border-gray-800 flex items-center justify-between">
            <h2 className="text-xl font-bold">{post ? 'Edit Post' : 'New Post'}</h2>
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Content</label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Share an update with your subscribers..."
                rows={4}
                className="w-full p-3 rounded-lg bg-gray-800 border border-gray-700 text-white resize-none"
                autoFocus
              />
            </div>

            <div>
              <label className="text-sm text-gray-400 mb-2 block">Image (Optional)</label>
              {imageUrl ? (
                <div className="relative">
                  <img src={imageUrl} alt="" className="rounded-lg max-h-48 object-cover" />
                  <button
                    type="button"
                    onClick={() => setImageUrl('')}
                    className="absolute top-2 right-2 p-1 bg-red-600 rounded-full hover:bg-red-500"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 p-8 border-2 border-dashed border-gray-700 rounded-lg cursor-pointer hover:bg-gray-800/50 transition">
                  {uploading ? (
                    <><Loader2 className="h-5 w-5 animate-spin" /> Uploading...</>
                  ) : (
                    <><ImageIcon className="h-5 w-5 text-gray-500" /> <span className="text-gray-400">Click to upload image</span></>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
              )}
            </div>
          </div>

          <div className="p-6 border-t border-gray-800 flex gap-3">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 border-gray-700">
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={saving || !content.trim()}
              className="flex-1 bg-gradient-to-r from-cyan-600 to-blue-600"
            >
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</> : 'Post'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatorDashboard;
